import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const r = await login(email, password);
        setSubmitting(false);
        if (!r.ok) {
            setError(r.error);
            toast.error(r.error);
            return;
        }
        toast.success("Welcome back");
        const role = r.user.role;
        navigate(
            role === "creator"
                ? "/creator"
                : role === "brand"
                  ? "/brand"
                  : role === "admin"
                    ? "/admin"
                    : "/",
        );
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 bg-white">
            <div className="hidden md:flex relative bg-slate-900 overflow-hidden flex-col justify-between p-12">
                <Link to="/" className="flex items-center gap-2" data-testid="back-home">
                    <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-lg">
                        c
                    </div>
                    <span className="font-display text-xl font-semibold text-white">castline</span>
                </Link>
                <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-coral/30 rounded-full blur-3xl" />
                <div className="absolute -top-20 -left-20 w-96 h-96 bg-lime/20 rounded-full blur-3xl" />
                <div className="relative">
                    <h2 className="font-display text-4xl md:text-5xl text-white font-semibold leading-tight tracking-tight max-w-md">
                        Make it once.{" "}
                        <span className="text-coral">Earn every time it performs.</span>
                    </h2>
                    <p className="text-slate-400 mt-5 max-w-sm">
                        Your videos become reusable ad inventory. Brands run them, you earn on
                        every channel that delivers.
                    </p>
                </div>
                <div className="relative text-xs text-slate-500">© 2026 Castline</div>
            </div>
            <div className="flex flex-col justify-center px-6 lg:px-16 py-12">
                <Link
                    to="/"
                    className="text-sm text-slate-500 hover:text-slate-900 mb-10 inline-flex items-center gap-1 md:hidden"
                >
                    ← Back
                </Link>
                <h1 className="font-display text-4xl font-semibold text-slate-900 mb-2">Log in</h1>
                <p className="text-sm text-slate-500 mb-10">Welcome back to Castline.</p>
                <form onSubmit={onSubmit} className="space-y-5 max-w-md" data-testid="login-form">
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="rounded-xl border-slate-200 h-12 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="login-email"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Password</Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="rounded-xl border-slate-200 h-12 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="login-password"
                        />
                    </div>
                    {error && (
                        <div className="text-sm text-red-500" data-testid="login-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-pill-primary w-full"
                        data-testid="login-submit"
                    >
                        {submitting ? "Logging in…" : "Log in →"}
                    </button>
                    <div className="text-sm text-slate-500">
                        No account?{" "}
                        <Link to="/register" className="text-coral font-semibold hover:underline">
                            Sign up
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
